import React, { FormEvent, useEffect, useMemo, useState } from 'react';
import { CalendarDays, Clock3, Pencil, Plus, Save, X } from 'lucide-react';
import { ClassPayload, ClassSession, createClass, getClasses, updateClass } from '../services/classService';
import { getPlans, Plan } from '../services/planService';
import { DateTimePicker } from './DateTimePicker';

interface ClassesSectionProps {
  token: string | null;
  isAdmin: boolean;
}

const blankForm: ClassPayload = {
  name: '',
  url: '',
  class_date: '',
  end_time: '',
  is_active: true,
  plan_ids: [],
};

const pad = (value: number): string => String(value).padStart(2, '0');

const toDateTimeInput = (value: string): string => {
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? '' : `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
};

const toTimeInput = (value: string): string => {
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? '' : `${pad(date.getHours())}:${pad(date.getMinutes())}`;
};

const formatClassDate = (value: string): string =>
  new Intl.DateTimeFormat(undefined, { dateStyle: 'medium' }).format(new Date(value));

const formatStartTime = (classSession: ClassSession): string =>
  new Intl.DateTimeFormat(undefined, { timeStyle: 'short' }).format(new Date(classSession.class_date));

const getClassEndDate = (classSession: ClassSession): Date => {
  return new Date(classSession.end_date);
};

const formatEndTime = (classSession: ClassSession): string =>
  new Intl.DateTimeFormat(undefined, { timeStyle: 'short' }).format(getClassEndDate(classSession));

export const ClassesSection: React.FC<ClassesSectionProps> = ({ token, isAdmin }) => {
  const [classes, setClasses] = useState<ClassSession[]>([]);
  const [plans, setPlans] = useState<Plan[]>([]);
  const [form, setForm] = useState<ClassPayload>(blankForm);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(Boolean(token));
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState('');
  const [classTab, setClassTab] = useState<'upcoming' | 'past'>('upcoming');

  const planNames = useMemo(() => new Map(plans.map((plan) => [plan.id, plan.name])), [plans]);
  const premiumPlanId = useMemo(
    () => plans.find((plan) => plan.name.trim().toLowerCase() === 'premium')?.id,
    [plans],
  );
  const upcomingClasses = useMemo(() => classes.filter((classSession) => new Date() <= getClassEndDate(classSession)), [classes]);
  const pastClasses = useMemo(() => classes.filter((classSession) => new Date() > getClassEndDate(classSession)), [classes]);

  const loadClasses = async () => {
    if (!token) return;
    setIsLoading(true);
    setError('');
    try {
      const [classRows, planResponse] = await Promise.all([
        getClasses(token),
        isAdmin ? getPlans() : Promise.resolve(null),
      ]);
      setClasses(classRows);
      if (planResponse) {
        setPlans(planResponse.data);
      }
    } catch (loadError) {
      setError(loadError instanceof Error ? loadError.message : 'Unable to load classes.');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    void loadClasses();
  // The token changes only when the signed-in user changes.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, isAdmin]);

  const resetForm = () => {
    setForm({ ...blankForm, plan_ids: [] });
    setEditingId(null);
    setIsFormOpen(false);
    setError('');
  };

  const openCreateForm = () => {
    setForm({ ...blankForm, plan_ids: premiumPlanId === undefined ? [] : [premiumPlanId] });
    setEditingId(null);
    setIsFormOpen(true);
    setError('');
  };

  const openEditForm = (classSession: ClassSession) => {
    setForm({
      name: classSession.name,
      url: classSession.url ?? '',
      class_date: toDateTimeInput(classSession.class_date),
      end_time: toTimeInput(classSession.end_date),
      is_active: classSession.is_active,
      plan_ids: premiumPlanId === undefined || classSession.plan_ids.includes(premiumPlanId)
        ? classSession.plan_ids
        : [...classSession.plan_ids, premiumPlanId],
    });
    setEditingId(classSession.id);
    setIsFormOpen(true);
    setError('');
  };

  const submitForm = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!token) return;
    if (!form.class_date) {
      setError('Select the live class date and time.');
      return;
    }
    if (!form.end_time) {
      setError('Select the live class end time.');
      return;
    }
    if (premiumPlanId === undefined) {
      setError('The Premium plan is not available.');
      return;
    }
    const selectedPlanIds = Array.from(new Set([...form.plan_ids, premiumPlanId]));
    if (selectedPlanIds.length === 0) {
      setError('Select at least one plan.');
      return;
    }

    setIsSaving(true);
    setError('');
    try {
      if (editingId === null) {
        await createClass(token, { ...form, plan_ids: selectedPlanIds });
      } else {
        await updateClass(token, editingId, { ...form, plan_ids: selectedPlanIds });
      }
      resetForm();
      await loadClasses();
    } catch (saveError) {
      setError(saveError instanceof Error ? saveError.message : 'Unable to save class.');
    } finally {
      setIsSaving(false);
    }
  };

  if (!token || !isAdmin) return null;

  return (
    <section className="mb-12 rounded-[2rem] border border-zinc-200 bg-white p-6 shadow-xl shadow-zinc-200/40 md:p-8">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <div className="flex items-center gap-2 text-amber-500">
            <CalendarDays className="h-5 w-5" />
            <span className="text-xs font-black uppercase tracking-widest">Live learning</span>
          </div>
          <h2 className="mt-2 text-2xl font-black tracking-tight text-zinc-900">Live Classes</h2>
          <p className="mt-1 text-sm text-zinc-500">Manage scheduled live classes for every plan.</p>
        </div>
        {isAdmin && (
          <button
            type="button"
            onClick={openCreateForm}
            className="inline-flex min-h-11 items-center justify-center gap-2 rounded-xl bg-zinc-950 px-4 py-3 text-xs font-black uppercase tracking-widest text-white transition-colors hover:bg-zinc-800"
          >
            <Plus className="h-4 w-4" /> Add class
          </button>
        )}
      </div>

      {error && <p className="mt-5 rounded-xl bg-red-50 px-4 py-3 text-sm font-medium text-red-700">{error}</p>}

      {isAdmin && isFormOpen && (
        <form onSubmit={submitForm} className="mt-6 grid gap-4 rounded-2xl border border-zinc-200 bg-zinc-50 p-5 md:grid-cols-2">
          <label className="md:col-span-2">
            <span className="mb-1 block text-xs font-bold uppercase tracking-wide text-zinc-600">Class name</span>
            <input required type="text" maxLength={150} value={form.name} onChange={(event) => setForm((value) => ({ ...value, name: event.target.value }))} className="w-full rounded-xl border border-zinc-300 bg-white px-3 py-2.5 text-sm" placeholder="e.g. AI Strategy Live Session" />
          </label>
          <label className="md:col-span-2">
            <span className="mb-1 block text-xs font-bold uppercase tracking-wide text-zinc-600">Live class URL</span>
            <input required type="url" value={form.url} onChange={(event) => setForm((value) => ({ ...value, url: event.target.value }))} className="w-full rounded-xl border border-zinc-300 bg-white px-3 py-2.5 text-sm" placeholder="https://…" />
          </label>
          <DateTimePicker id="class-date" label="Live class date and time" value={form.class_date} onChange={(class_date) => setForm((value) => ({ ...value, class_date }))} />
          <label>
            <span className="mb-1 block text-xs font-bold uppercase tracking-wide text-zinc-600">Live class end time</span>
            <input required type="time" value={form.end_time} onChange={(event) => setForm((value) => ({ ...value, end_time: event.target.value }))} className="min-h-11 w-full rounded-xl border border-zinc-300 bg-white px-3 py-2.5 text-sm" />
          </label>
          <fieldset>
            <legend className="mb-1 block text-xs font-bold uppercase tracking-wide text-zinc-600">Plans</legend>
            <div className="flex min-h-11 flex-wrap items-center gap-1.5 rounded-xl border border-zinc-300 bg-white px-2 py-1">
              {plans.map((plan) => {
                const isPremium = plan.id === premiumPlanId;
                const checked = isPremium || form.plan_ids.includes(plan.id);
                return (
                  <label key={plan.id} className={`inline-flex items-center gap-1.5 rounded-lg px-2 py-1.5 text-xs font-bold ${isPremium ? 'cursor-default' : 'cursor-pointer'} ${checked ? 'bg-amber-100 text-amber-900' : 'bg-zinc-50 text-zinc-600'}`}>
                    <input type="checkbox" checked={checked} disabled={isPremium} onChange={() => setForm((value) => ({ ...value, plan_ids: checked ? value.plan_ids.filter((id) => id !== plan.id) : [...value.plan_ids, plan.id] }))} className="h-3.5 w-3.5 accent-amber-500 disabled:opacity-100" />
                    {plan.name}
                  </label>
                );
              })}
            </div>
          </fieldset>
          <label className="flex items-end gap-3 pb-2 text-sm font-bold text-zinc-700">
            <input type="checkbox" checked={form.is_active} onChange={(event) => setForm((value) => ({ ...value, is_active: event.target.checked }))} className="h-4 w-4 accent-amber-500" />
            Active and visible to learners
          </label>
          <div className="flex gap-3 md:col-span-2">
            <button disabled={isSaving} className="inline-flex min-h-11 items-center gap-2 rounded-xl bg-amber-500 px-4 py-3 text-xs font-black uppercase tracking-widest text-zinc-950 disabled:opacity-60">
              <Save className="h-4 w-4" /> {isSaving ? 'Saving…' : editingId === null ? 'Create class' : 'Save changes'}
            </button>
            <button type="button" onClick={resetForm} className="inline-flex min-h-11 items-center gap-2 rounded-xl border border-zinc-300 px-4 py-3 text-xs font-black uppercase tracking-widest text-zinc-700">
              <X className="h-4 w-4" /> Cancel
            </button>
          </div>
        </form>
      )}

      <div className="mt-8 space-y-5">
        {isLoading ? <p className="text-sm text-zinc-500">Loading live classes…</p> : (
          <>
            <div className="inline-flex rounded-xl border border-zinc-200 bg-zinc-100 p-1" role="tablist" aria-label="Live class status">
              <button
                type="button"
                role="tab"
                aria-selected={classTab === 'upcoming'}
                onClick={() => setClassTab('upcoming')}
                className={`inline-flex min-h-10 items-center gap-2 rounded-lg px-4 text-xs font-black uppercase tracking-wider transition-all ${classTab === 'upcoming' ? 'bg-zinc-950 text-white shadow-sm' : 'text-zinc-500 hover:text-zinc-900'}`}
              >
                Active & upcoming <span className={`rounded-full px-1.5 py-0.5 text-[10px] ${classTab === 'upcoming' ? 'bg-white/15 text-white' : 'bg-white text-zinc-600'}`}>{upcomingClasses.length}</span>
              </button>
              <button
                type="button"
                role="tab"
                aria-selected={classTab === 'past'}
                onClick={() => setClassTab('past')}
                className={`inline-flex min-h-10 items-center gap-2 rounded-lg px-4 text-xs font-black uppercase tracking-wider transition-all ${classTab === 'past' ? 'bg-zinc-950 text-white shadow-sm' : 'text-zinc-500 hover:text-zinc-900'}`}
              >
                Past classes <span className={`rounded-full px-1.5 py-0.5 text-[10px] ${classTab === 'past' ? 'bg-white/15 text-white' : 'bg-white text-zinc-600'}`}>{pastClasses.length}</span>
              </button>
            </div>

            {classTab === 'upcoming' && (
            <section role="tabpanel" aria-label="Active and upcoming live classes">
              <div className="flex items-start justify-between gap-4">
                <div>
                  <h3 className="text-sm font-black uppercase tracking-widest text-zinc-900">Active & upcoming live classes</h3>
                  <p className="mt-1 text-sm text-zinc-500">Classes stay here until their scheduled end time.</p>
                </div>
                <span className="mt-1.5 h-2.5 w-2.5 rounded-full bg-emerald-500" aria-hidden="true" />
              </div>
              <div className="mt-4 grid gap-4 md:grid-cols-2 xl:grid-cols-3">
                {upcomingClasses.length === 0 ? <p className="text-sm text-zinc-500">No upcoming live classes.</p> : upcomingClasses.map((classSession) => (
          <article key={classSession.id} className={`rounded-2xl border p-5 ${classSession.is_active ? 'border-zinc-200 bg-white' : 'border-zinc-200 bg-zinc-50 opacity-70'}`}>
                    <p className="text-xs font-black uppercase tracking-widest text-amber-600">{classSession.plan_ids.map((id) => planNames.get(id) ?? `Plan #${id}`).join(' · ')}</p>
            <h3 className="mt-3 text-base font-black text-zinc-900">{classSession.name}</h3>
            <div className="mt-4 flex items-center gap-2 text-sm font-bold text-zinc-700"><CalendarDays className="h-4 w-4 text-amber-500" /> {formatClassDate(classSession.class_date)}</div>
            <div className="mt-3 grid grid-cols-2 gap-2">
              <div className="rounded-xl bg-zinc-100 px-3 py-2.5">
                <span className="block text-[10px] font-black uppercase tracking-wider text-zinc-500">Starts at</span>
                <span className="mt-0.5 flex items-center gap-1.5 text-sm font-black text-zinc-900"><Clock3 className="h-3.5 w-3.5 text-amber-500" /> {formatStartTime(classSession)}</span>
              </div>
              <div className="rounded-xl bg-amber-50 px-3 py-2.5">
                <span className="block text-[10px] font-black uppercase tracking-wider text-amber-700">Ends at</span>
                <span className="mt-0.5 flex items-center gap-1.5 text-sm font-black text-amber-900"><Clock3 className="h-3.5 w-3.5 text-amber-500" /> {formatEndTime(classSession)}</span>
              </div>
            </div>
            {!classSession.is_active && <p className="mt-3 text-xs font-bold uppercase tracking-wide text-zinc-500">Inactive</p>}
            <div className="mt-5 flex flex-wrap gap-3">
                  <button type="button" onClick={() => openEditForm(classSession)} className="inline-flex items-center gap-1 text-sm font-bold text-zinc-600 hover:text-zinc-950"><Pencil className="h-4 w-4" /> Edit</button>
            </div>
          </article>
                ))}
              </div>
            </section>
            )}

            {classTab === 'past' && (
            <section role="tabpanel" aria-label="Past live classes">
              <div className="flex items-start justify-between gap-4">
                <div>
                  <h3 className="text-sm font-black uppercase tracking-widest text-zinc-700">Past live classes</h3>
                  <p className="mt-1 text-sm text-zinc-500">Classes appear here after their scheduled end time.</p>
                </div>
                <span className="mt-1.5 h-2.5 w-2.5 rounded-full bg-red-400" aria-hidden="true" />
              </div>
              <div className="mt-4 grid gap-4 md:grid-cols-2 xl:grid-cols-3">
                {pastClasses.length === 0 ? <p className="text-sm text-zinc-500">No past live classes.</p> : pastClasses.map((classSession) => (
                  <article key={classSession.id} className="rounded-2xl border border-zinc-200 bg-zinc-50 p-5 opacity-75">
                    <p className="text-xs font-black uppercase tracking-widest text-zinc-500">{classSession.plan_ids.map((id) => planNames.get(id) ?? `Plan #${id}`).join(' · ')}</p>
                    <h3 className="mt-3 text-base font-black text-zinc-900">{classSession.name}</h3>
                    <div className="mt-4 flex items-center gap-2 text-sm font-bold text-zinc-600"><CalendarDays className="h-4 w-4 text-zinc-400" /> {formatClassDate(classSession.class_date)}</div>
                    <div className="mt-3 grid grid-cols-2 gap-2">
                      <div className="rounded-xl bg-white px-3 py-2.5">
                        <span className="block text-[10px] font-black uppercase tracking-wider text-zinc-500">Starts at</span>
                        <span className="mt-0.5 flex items-center gap-1.5 text-sm font-black text-zinc-700"><Clock3 className="h-3.5 w-3.5 text-zinc-400" /> {formatStartTime(classSession)}</span>
                      </div>
                      <div className="rounded-xl bg-white px-3 py-2.5">
                        <span className="block text-[10px] font-black uppercase tracking-wider text-zinc-500">Ends at</span>
                        <span className="mt-0.5 flex items-center gap-1.5 text-sm font-black text-zinc-700"><Clock3 className="h-3.5 w-3.5 text-zinc-400" /> {formatEndTime(classSession)}</span>
                      </div>
                    </div>
                    <div className="mt-5 flex flex-wrap gap-3">
                      <button type="button" onClick={() => openEditForm(classSession)} className="inline-flex items-center gap-1 text-sm font-bold text-zinc-600 hover:text-zinc-950"><Pencil className="h-4 w-4" /> Edit</button>
                    </div>
                  </article>
                ))}
              </div>
            </section>
            )}
          </>
        )}
      </div>
    </section>
  );
};
